import { DOCUMENT, DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, signal, TemplateRef, untracked, viewChild } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../../../core/services/notification.service';
import { AuthService } from '../../../core/auth/services/auth.service';
import { NOTIFICATION_ICONS, NOTIFICATION_LABELS } from '../../../core/models/system-notification';
import { CustomSnackbarComponent } from '../custom-snackbar/custom-snackbar.component';
import { MessageIconComponent } from './message-icon.component';

@Component({
  selector: 'app-system-messages',
  imports: [DatePipe, MatDialogModule, CustomSnackbarComponent, MessageIconComponent],
  templateUrl: './system-messages.component.html',
  styleUrl: './system-messages.component.scss',
  host: { '(document:visibilitychange)': 'visibilityChanged()' },
})
export class SystemMessagesComponent {
  readonly notify = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly document = inject(DOCUMENT);
  private readonly auth = inject(AuthService);
  private readonly panel = viewChild.required<TemplateRef<unknown>>('panel');
  private dialogRef?: MatDialogRef<unknown>;
  readonly filter = signal<'unread' | 'all'>('unread');
  readonly visibleItems = computed(() => this.notify.notifications().filter(item => this.filter() === 'all' || !item.read));
  readonly hasRead = computed(() => this.notify.notifications().some(item => item.read));
  readonly icons = NOTIFICATION_ICONS;
  readonly labels = NOTIFICATION_LABELS;

  constructor() {
    let identity = this.identity();
    effect(() => {
      const next = this.identity();
      if (next !== identity) {
        identity = next;
        untracked(() => {
          const expired = !this.auth.isLoggedIn && this.notify.notifications().some(item => !item.read && item.status === 401);
          this.dialogRef?.close();
          this.notify.reset();
          if (expired) this.notify.warning('Votre session a expiré. Veuillez vous reconnecter.', 'Session expirée', [], { source: 'Connexion' });
        });
      }
    });
    this.visibilityChanged();
    inject(DestroyRef).onDestroy(() => this.dialogRef?.close());
  }

  open(): void {
    if (this.dialogRef) return;
    this.filter.set(this.notify.unreadCount() ? 'unread' : 'all');
    this.notify.setCenterOpen(true);
    this.dialogRef = this.dialog.open(this.panel(), {
      width: '520px', maxWidth: '100vw', height: '100dvh', maxHeight: '100dvh',
      position: { right: '0', top: '0' }, ariaLabelledBy: 'system-messages-title',
      autoFocus: '.panel-close', restoreFocus: true,
    });
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
      this.notify.setCenterOpen(false);
    });
  }

  focusLeft(event: FocusEvent): void {
    const container = event.currentTarget as HTMLElement;
    if (!container.contains(event.relatedTarget as Node | null)) this.notify.pausePreview('focus', false);
  }

  visibilityChanged(): void {
    this.notify.pausePreview('hidden', this.document.hidden);
  }

  private identity(): string {
    return `${this.auth.isLoggedIn}:${this.auth.keyCloakId}:${this.auth.organizationId}`;
  }
}
